/**
 * Which tenant the console is looking at.
 *
 * The server scopes every read by membership and refuses to guess when an account
 * belongs to more than one tenant (ADR-0008). The client therefore has to name one,
 * and it has to be visible on the screen while it does: a queue that silently shows
 * one customer's alerts while the operator believes they are looking at another is
 * the worst kind of correct.
 */
import { useCallback, useSyncExternalStore } from 'react'

const STORAGE_KEY = 'sentry.tenant.v1'
const listeners = new Set<() => void>()

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function snapshot(): string {
  return localStorage.getItem(STORAGE_KEY) ?? ''
}

export function useTenant(available: string[]): [string, (slug: string) => void] {
  const stored = useSyncExternalStore(subscribe, snapshot, () => '')
  // A stored tenant the account no longer has access to is ignored rather than sent:
  // memberships change, and a stale value would produce a 404 on every screen with
  // no clue why.
  const current = available.includes(stored) ? stored : (available[0] ?? '')

  const choose = useCallback((slug: string) => {
    localStorage.setItem(STORAGE_KEY, slug)
    for (const listener of listeners) listener()
  }, [])

  return [current, choose]
}
